import * as THREE from "three";

export const webglWaveInit = () => {
  const canvases = document.querySelectorAll(".gl_wave");
  if (!canvases.length) return; // 1枚もなければ何もしない

  // === 各canvasの設定をまとめて保持 ===
  const scenes = [];

  canvases.forEach((canvas) => {
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0); // 完全透明にクリア

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const uniforms = {
      u_time: { value: 0 },
      u_resolution: { value: new THREE.Vector2(1, 1) },
      u_scale: { value: 3.5 },
      u_speed: { value: 0.4 },
      u_threshold: { value: 0.5 },
      u_width: { value: 0.015 },
      u_stretch: { value: 0.5 },
      u_colorB: { value: new THREE.Vector4(0.0, 0.0, 0.0, 0.0) },
      u_colorA: { value: new THREE.Vector4(0.4667, 0.8196, 0.9373, 1.0) },
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      transparent: true,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision mediump float;
        uniform float u_time, u_scale, u_speed, u_threshold, u_width, u_stretch;
        uniform vec2  u_resolution;
        uniform vec4  u_colorA, u_colorB;
        varying vec2  vUv;

        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

        float snoise(vec2 v) {
          const vec4 C = vec4(0.211324865405187,
                              0.366025403784439,
                              -0.577350269189626,
                              0.024390243902439);
          vec2 i  = floor(v + dot(v, C.yy));
          vec2 x0 = v - i + dot(i, C.xx);
          vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
          vec4 x12 = x0.xyxy + C.xxzz;
          x12.xy -= i1;
          i = mod289(i);
          vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
                  + i.x + vec3(0.0, i1.x, 1.0));
          vec3 x = fract(p * C.w) * 2.0 - 1.0;
          vec3 h = abs(x) - 0.5;
          vec3 ox = floor(x + 0.5);
          vec3 a0 = x - ox;
          vec2 g0 = vec2(a0.x, h.x);
          vec2 g1 = vec2(a0.y, h.y);
          vec2 g2 = vec2(a0.z, h.z);
          float t0 = 0.5 - dot(x0, x0);
          float t1 = 0.5 - dot(x12.xy, x12.xy);
          float t2 = 0.5 - dot(x12.zw, x12.zw);
          float n0 = (t0 < 0.0) ? 0.0 : pow(t0, 4.0) * dot(g0, x0);
          float n1 = (t1 < 0.0) ? 0.0 : pow(t1, 4.0) * dot(g1, x12.xy);
          float n2 = (t2 < 0.0) ? 0.0 : pow(t2, 4.0) * dot(g2, x12.zw);
          return 70.0 * (n0 + n1 + n2);
        }

        float fbm(vec2 p){
          float v=0.0; float a=0.5;
          for(int i=0;i<3;i++){
            v+=a*snoise(p);
            p*=1.6;
            a*=0.5;
          }
          return v*0.5+0.5;
        }

        void main(){
          vec2 st = vUv - 0.5;
          st.x *= u_resolution.x / u_resolution.y;
          st *= u_scale;
          float k = u_stretch;
          mat2 S = mat2(k, 0.0, 0.0, 1.0 / k);
          st = S * st;
          float t = u_time * u_speed;

          vec2 q = vec2(
            fbm(st + vec2(0.0, 0.0) + t),
            fbm(st + vec2(5.2, 1.3) + t)
          );
          vec2 r = vec2(
            fbm(st + 0.3*q + vec2(1.7, 9.2) - t*0.6),
            fbm(st + 0.3*q + vec2(8.3, 2.8) + t*0.6)
          );
          float f = fbm(st + 0.5*r);
          float band = step(u_threshold - u_width, f) - step(u_threshold + u_width, f);
          vec4 col = mix(u_colorB, u_colorA, band);
          gl_FragColor = col;
        }
      `,
    });

    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    scene.add(mesh);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      renderer.setSize(w, h, false);
      uniforms.u_resolution.value.set(w, h);
    };
    resize();

    // 各canvasの情報を保存（可視状態フラグも持つ）
    scenes.push({
      renderer,
      scene,
      camera,
      uniforms,
      resize,
      visible: false, // ← IntersectionObserverで切り替え
      canvas,
    });
  });

  // === IntersectionObserverで可視状態を監視 ===
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const target = scenes.find((s) => s.canvas === entry.target);
        if (target) target.visible = entry.isIntersecting;
      });
    },
    { threshold: 0.05 } // 5%見えたらONにする
  );

  scenes.forEach((s) => observer.observe(s.canvas));

  // === リサイズ処理 ===
  window.addEventListener("resize", () => {
    scenes.forEach((s) => s.resize());
  });

  // === 1ループで同期描画（可視canvasのみ） ===
  const start = performance.now();
  const tick = (now) => {
    const elapsed = (now - start) * 0.001;
    scenes.forEach(({ renderer, scene, camera, uniforms, visible }) => {
      if (!visible) return; // 可視のときだけ描画
      uniforms.u_time.value = elapsed;
      renderer.render(scene, camera);
    });
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};
